import { GridColDef } from "@mui/x-data-grid";
import { Option } from "../../../../../constants/common";
import { applyDefaultColumnSettings } from "../../../../../utils/utils";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface AppointmentReportFilterData {
  date_range?: string;
  startDate?: string;
  endDate?: string;
  provider_Name?: string;
  location?: string;
  appointment_Mode?: string;
  appointment_Status?: string;
  scheduled_Source?: string;
  diagnosis?: string;
  search_Patient?: string;
}

export const DEFAULT_REPORT_FILTERS: AppointmentReportFilterData = {
  date_range: undefined,
  startDate: undefined,
  endDate: undefined,
  provider_Name: undefined,
  location: undefined,
  appointment_Mode: undefined,
  appointment_Status: undefined,
  scheduled_Source: undefined,
  diagnosis: undefined,
  search_Patient: undefined,
};

// ─── Status Color Mapping ────────────────────────────────────────────────────

export const REPORT_STATUS_MAP: Record<
  string,
  { label: string; color: string; bgColor: string }
> = {
  COMPLETED: { label: "Signed Off", color: "#049B22", bgColor: "#E1FCDE" },
  CONFIRMED: { label: "Confirmed", color: "#588BC1", bgColor: "#DCE6F7" },
  SCHEDULED: { label: "Scheduled", color: "#0097F0", bgColor: "#D0E5FF" },
  PENDING: { label: "Scheduled", color: "#0097F0", bgColor: "#D0E5FF" },
  CANCELLED: { label: "Canceled", color: "#FF3939", bgColor: "#FFE1E0" },
  NO_SHOW: { label: "No Show", color: "#B93815", bgColor: "#FEF6EE" },
  CHECKED_IN: { label: "Checked In", color: "#7B61FF", bgColor: "#EDE8FF" },
  IN_EXAM: { label: "In Exam", color: "#00B8D9", bgColor: "#E0F7FA" },
  INSTANT: { label: "Instant", color: "#0B8A5C", bgColor: "#DFF5EA" },
  INTAKE_COMPLETED: {
    label: "Intake Completed",
    color: "#36B37E",
    bgColor: "#E3FCEF",
  },
  IN_PROGRESS: {
    label: "In Progress",
    color: "#943C00",
    bgColor: "#FFF2D2",
  },
};

// ─── Mode Mapping ────────────────────────────────────────────────────────────

export const REPORT_MODE_MAP: Record<string, { label: string; color: string; bgColor: string }> =
  {
    IN_PERSON: { label: "In Person", color: "#004AB1", bgColor: "#E0EDFF" },
    VIRTUAL: { label: "Telehealth", color: "#3538CD", bgColor: "#EEF4FF" },
  };

// ─── Status filter options ───────────────────────────────────────────────────

/**
 * Status options mirror the app-wide appointment statuses defined in
 * `STATUS_APPOINTMENT` (constants/master.ts) so the filter offers every status
 * that can appear in the report, using the same terminology as the rest of the app.
 */
export const APPOINTMENT_STATUS_OPTIONS: Option[] = [
  { value: "ALL_STATUSES", label: "All Statuses" },
  { value: "PENDING", label: "Scheduled" },
  { value: "CHECKED_IN", label: "Checked In" },
  { value: "IN_EXAM", label: "In Exam" },
  { value: "COMPLETED", label: "Signed Off" },
  { value: "CONFIRMED", label: "Confirmed" },
  { value: "NO_SHOW", label: "No Show" },
  { value: "CANCELLED", label: "Canceled" },
  { value: "INSTANT", label: "Instant" },
];

export const APPOINTMENT_MODE_OPTIONS: Option[] = [
  {value: "ALL_MODE" , label: "All Mode"},
  { value: "IN_PERSON", label: "In-Person" },
  { value: "VIRTUAL", label: "Telehealth" },
];

export const REPORT_TIME_INTERVAL_OPTIONS: Option[] = [
  { value: "TODAY", label: "Today" },
  { value: "THIS_WEEK", label: "This Week" },
  { value: "THIS_MONTH", label: "This Month" },
  { value: "LAST_MONTH", label: "Last Month" },
  { value: "LAST_3_MONTHS", label: "Last 3 Months" },
  { value: "LAST_6_MONTHS", label: "Last 6 Months" },
  { value: "THIS_YEAR", label: "This Year" },
];

export const APPOINTMENT_SOURCE_OPTIONS: Option[] = [
  { value: "ALL_SOURCE", label: "All Sources" },
  { value: "Provider Scheduled", label: "Provider Scheduled" },
  { value: "Patient Portal", label: "Patient Portal" },
  // { value: "FRONT_DESK", label: "Front Desk" },
  // { value: "EXTERNAL_REFERRAL", label: "External Referral" },
];

// ─── Report Type Options ────────────────────────────────────────────────────

export const REPORT_TYPE_OPTIONS: Option[] = [
  { value: "patient-encounters", label: "Patient Visits Report" },
  { value: "appointment-scheduling", label: "Appointment & Scheduling Report" },
  // ECRH-4758: Hide unimplemented report types
  // { value: "medication", label: "Medication Report" },
  // { value: "problem", label: "Problem Report" },
  // { value: "allergy", label: "Allergy Report" },
  // { value: "encounter", label: "Visit Reports" },
  // { value: "drug-interactions", label: "Drug Interactions" },
  // { value: "referral", label: "Referral Reports" },
];

// ─── Table Column Definitions ────────────────────────────────────────────────

const SORTABLE_FIELDS = ["startTime", "scheduledOn", "status"];

export const getReportColumns = (
  renderStatusCell: (params: any) => JSX.Element,
  renderModeCell: (params: any) => JSX.Element,
  renderPatientName: (params: any) => JSX.Element
): GridColDef[] =>
  applyDefaultColumnSettings(
    [
      {
        field: "appointmentId",
        headerName: "Visit ID",
        flex: 1,
        minWidth: 130,
      },
      {
        field: "startTime",
        headerName: "Date & Time",
        flex: 1.2,
        minWidth: 170,
      },
      {
        field: "patientName",
        headerName: "Patient Name",
        flex: 1.2,
        minWidth: 150,
        renderCell: renderPatientName,
      },
      {
        field: "ecarehealthPatientId",
        headerName: "Patient ID",
        flex: 0.8,
        minWidth: 110,
      },
      {
        field: "providerName",
        headerName: "Provider",
        flex: 1,
        minWidth: 130,
      },
      {
        field: "type",
        headerName: "Type",
        flex: 0.9,
        minWidth: 110,
        renderCell: renderModeCell,
      },
      {
        field: "locationName",
        headerName: "Location",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "scheduleSource",
        headerName: "Schedule Source",
        flex: 1,
        minWidth: 140,
      },
      {
        field: "scheduledOn",
        headerName: "Scheduled On",
        flex: 1,
        minWidth: 130,
      },
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        minWidth: 120,
        renderCell: renderStatusCell,
      },
      {
        field: "insuranceName",
        headerName: "Insurance",
        flex: 1,
        minWidth: 130,
      },
    ],
    SORTABLE_FIELDS
  );

// ─── KPI Card Definitions ────────────────────────────────────────────────────

export interface KPICardConfig {
  key: string;
  label: string;
  valueKey: string;
  changeKey: string;
  format: "number" | "percent";
  subtext?: string;
}

export const KPI_CARDS: KPICardConfig[] = [
  {
    key: "total",
    label: "Total Appointments",
    valueKey: "totalAppointments",
    changeKey: "totalAppointmentsChange",
    format: "number",
    subtext: "vs last month",
  },
  {
    key: "completion",
    label: "Completion Rate",
    valueKey: "completionRate",
    changeKey: "completionRateChange",
    format: "percent",
    subtext: "vs last month",
  },
  {
    key: "noshow",
    label: "No Show Rate",
    valueKey: "noShowRate",
    changeKey: "noShowRateChange",
    format: "percent",
    subtext: "vs last month",
  },
  {
    key: "cancellation",
    label: "Cancellation Rate",
    valueKey: "cancellationRate",
    changeKey: "cancellationRateChange",
    format: "percent",
    subtext: "vs last month",
  },
];



